import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecentNotifications, markAsRead, markAsViewed } from '../services/notificationService';
import { useNotifications } from '../utils/NotificationContext';
import { onNewNotification } from '../services/socketService';

const NotificationDropdown = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { notificationCount, updateNotificationCount } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      markAllAsSeen();
    }
  }, [isOpen]);

  const markAllAsSeen = async () => {
    try {
      await markAsViewed();
      updateNotificationCount({
        ...notificationCount,
        unviewed: 0
      });
    } catch (error) {
      console.error('Error marking notifications as seen:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onNewNotification((notification) => {
      setNotifications(prev => [notification, ...prev].slice(0, 10));
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await getRecentNotifications(10);
      console.log('NotificationDropdown: fetchNotifications response:', response);
      const notificationsArray = Array.isArray(response?.data) ? response.data : [];
      setNotifications(notificationsArray);
    } catch (error) {
      console.error('NotificationDropdown: Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.read) {
        try {
          await markAsRead(notification.id);
          setNotifications(prev =>
            prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
          );
          updateNotificationCount({
            ...notificationCount,
            unread: Math.max(0, (notificationCount.unread || 0) - 1),
            read: (notificationCount.read || 0) + 1
          });
        } catch (error) {
          console.error('Error marking notification as read:', error);
        }
      }

      // Extract POI ID from various possible fields
      const poiId =
        notification.poi_id ||
        notification.poiId ||
        notification.data?.poi_id ||
        notification.data?.poiId ||
        notification.poi?.id;

      // Always navigate to POI detail page
      if (poiId) {
        navigate(`/pois?view=${poiId}`);
      } else {
        navigate('/pois');
      }
      onClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleViewAll = () => {
    navigate('/notifications');
    onClose();
  };

  const getNotificationIcon = (type) => {
    const icons = {
      order: '🛒',
      alert: '⚠️',
      user: '👤',
      update: '📡',
      success: '✅',
      security: '🔒',
      payment: '💰',
      maintenance: '🔧',
      report: '📊',
      approval: '✋'
    };
    return icons[type] || '🔔';
  };

  const getNotificationColor = (type) => {
    const colors = {
      order: '#66B2FF',
      alert: '#FF5C5C',
      user: '#8B5CF6',
      update: '#F59E0B',
      success: '#2ECC71',
      security: '#FF5C5C',
      payment: '#2ECC71',
      maintenance: '#F59E0B',
      report: '#66B2FF',
      approval: '#8B5CF6'
    };
    return colors[type] || '#66B2FF';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="notification-dropdown"
      style={{
        position: 'fixed',
        top: 64,
        right: 32,
        width: 380,
        maxHeight: 600,
        background: 'white',
        borderRadius: 12,
        border: '1px solid #E8EDF3',
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideDown 0.2s ease-out'
      }}
    >
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          padding: '16px 18px',
          borderBottom: '1px solid #F0F4F8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>
          Notifications
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 18,
            color: 'var(--grey)'
          }}
        >
          ✕
        </button>
      </div>

      {/* Notifications List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0
        }}
      >
        {loading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '3px solid #E8EDF3',
              borderTop: '3px solid #66B2FF',
              animation: 'spin 0.8s linear infinite'
            }}>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
            <div style={{ fontSize: 13, color: 'var(--grey)', fontWeight: 500 }}>Loading notifications...</div>
          </div>
        ) : notifications.length === 0 ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: 'var(--grey)',
              fontSize: 13
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 10 }}>🔔</div>
            <div>No notifications yet</div>
          </div>
        ) : (
          notifications.map((notification, index) => (
            <div
              key={notification.id || index}
              onClick={() => handleNotificationClick(notification)}
              style={{
                padding: '14px 16px',
                borderBottom: index === notifications.length - 1 ? 'none' : '1px solid #F0F4F8',
                cursor: 'pointer',
                background: notification.read ? 'white' : '#F8FBFF',
                transition: 'background 0.2s',
                ':hover': { background: '#F5F7FA' }
              }}
              onMouseEnter={(e) => e.target.style.background = '#F5F7FA'}
              onMouseLeave={(e) => e.target.style.background = notification.read ? 'white' : '#F8FBFF'}
            >
              <div style={{ display: 'flex', gap: 12 }}>
                {/* Icon */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: `${getNotificationColor(notification.type)}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    flexShrink: 0
                  }}
                >
                  {getNotificationIcon(notification.type)}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 8
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: 'var(--navy)',
                        lineHeight: 1.3
                      }}
                    >
                      {notification.title}
                    </div>
                    {!notification.read && (
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#66B2FF',
                          flexShrink: 0,
                          marginTop: 3
                        }}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--grey)',
                      marginTop: 4,
                      lineHeight: 1.3
                    }}
                  >
                    {notification.message}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--grey)',
                      marginTop: 6
                    }}
                  >
                    {formatTime(notification.createdAt)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {!loading && notifications.length > 0 && (
        <div
          style={{
            padding: '14px 16px',
            borderTop: '1px solid #F0F4F8',
            textAlign: 'center'
          }}
        >
          <button
            onClick={handleViewAll}
            style={{
              background: 'none',
              border: 'none',
              color: '#66B2FF',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'none'
            }}
          >
            View all notifications →
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;

